import { WorkbenchRow } from '../models/workbench-row.model'

/** The predicate that decides whether a row opens a given tran category's dialog. */
export type LoadoutCondition =
  | { op: 'in' | 'notIn'; field: string; values: readonly ConditionValue[] }
  | { op: 'eq' | 'notEq'; field: string; value: ConditionValue }
  | { op: 'isNull' | 'isNotNull'; field: string }
  | { op: 'contains'; field: string; value: string }
  | { op: 'and' | 'or'; conditions: readonly LoadoutCondition[] }
  | { op: 'not'; condition: LoadoutCondition }

export type ConditionValue = string | number | boolean

/** Every operator the document may name. Closed on purpose — see {@link evaluateLoadoutCondition}. */
export const LOADOUT_OPERATORS = [
  'in',
  'notIn',
  'eq',
  'notEq',
  'isNull',
  'isNotNull',
  'contains',
  'and',
  'or',
  'not',
] as const

export type LoadoutOperator = typeof LOADOUT_OPERATORS[number]

/** A row's value is blank when it is absent, null, or empty once trimmed. */
const isBlank = (value: unknown): boolean =>
  value == null || (typeof value === 'string' && value.trim() === '')

/** The one spelling rule for comparing a row's value against an authored one. */
const sameValue = (rowValue: unknown, authored: ConditionValue): boolean => {
  if (typeof authored === 'string') {
    if (isBlank(rowValue)) return false
    return fold(String(rowValue)) === fold(authored)
  }
  return rowValue === authored
}

const fold = (value: string): string =>
  value.trim().replace(/\s+/g, ' ').toLowerCase()

/**
 * Read the field a condition names, by the same rule the projection reads one.
 *
 * Exported because the block path reads a routed field too, and reading it any other way would let
 * "no condition matched" and "nothing to report" disagree about the same row.
 */
export const readConditionField = (row: WorkbenchRow, field: string): unknown =>
  field
    .split('.')
    .reduce<unknown>(
      (value, key) =>
        value && typeof value === 'object'
          ? (value as Record<string, unknown>)[key]
          : undefined,
      row.fields,
    )

/** Whether a row satisfies a condition. */
export function evaluateLoadoutCondition(
  condition: LoadoutCondition | undefined,
  row: WorkbenchRow,
): boolean {
  if (!condition) return false

  switch (condition.op) {
    case 'in':
      return condition.values.some(value =>
        sameValue(readConditionField(row, condition.field), value),
      )
    case 'notIn':
      return !condition.values.some(value =>
        sameValue(readConditionField(row, condition.field), value),
      )
    case 'eq':
      return sameValue(
        readConditionField(row, condition.field),
        condition.value,
      )
    case 'notEq':
      return !sameValue(
        readConditionField(row, condition.field),
        condition.value,
      )
    case 'isNull':
      return isBlank(readConditionField(row, condition.field))
    case 'isNotNull':
      return !isBlank(readConditionField(row, condition.field))
    case 'contains': {
      const value = readConditionField(row, condition.field)
      if (isBlank(value)) return false
      return fold(String(value)).includes(fold(condition.value))
    }
    case 'and':
      return (
        condition.conditions.length > 0 &&
        condition.conditions.every(entry =>
          evaluateLoadoutCondition(entry, row),
        )
      )
    case 'or':
      return condition.conditions.some(entry =>
        evaluateLoadoutCondition(entry, row),
      )
    case 'not':
      return !evaluateLoadoutCondition(condition.condition, row)
    default:
      warnUnknownOperator(condition)
      return false
  }
}

/**
 * Every row field a condition reads.
 *
 * The block path uses this to say WHY nothing opened without naming a field of its own: the fields
 * a document routes on are the document's business, not this module's.
 */
export function conditionFields(
  condition: LoadoutCondition | undefined,
): string[] {
  if (!condition) return []

  switch (condition.op) {
    case 'and':
    case 'or':
      return [...new Set(condition.conditions.flatMap(conditionFields))]
    case 'not':
      return conditionFields(condition.condition)
    default:
      return 'field' in condition ? [condition.field] : []
  }
}

const warned = new Set<string>()

/** One line per unrecognised operator, not one per row — a grid can evaluate thousands. */
const warnUnknownOperator = (condition: LoadoutCondition): void => {
  const op = String((condition as { op?: unknown }).op)
  if (warned.has(op)) return
  warned.add(op)
  console.error(
    `[transactions-2] loadout condition names an operator this UI cannot evaluate: "${op}". ` +
      `No dialog will open from it. Known operators: ${LOADOUT_OPERATORS.join(
        ', ',
      )}.`,
  )
}
